// ServicesPage.jsx
import React, { useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Code, Tablet, Cpu, BarChart3, Check, 
  MoveRight, MessageCircle, Calendar 
} from 'lucide-react';

const ServicesPage = () => {
  const navigate = useNavigate();
  
  // Create refs for each section
  const webDevRef = useRef(null);
  const appDevRef = useRef(null);
  const aiRef = useRef(null);
  const marketingRef = useRef(null);
  const pricingRef = useRef(null);
  const contactRef = useRef(null);

  // Scroll to specific section function
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  };

  // Handle Get Started click - Navigate to /contact
  const handleGetStarted = () => {
    navigate('/contact');
  };

  // Handle service button clicks - Scroll to respective sections
  const handleExploreWebServices = () => {
    scrollToSection(webDevRef);
  };

  const handleExploreAppServices = () => {
    scrollToSection(appDevRef);
  };

  const handleExploreAISolutions = () => {
    scrollToSection(aiRef);
  };

  const handleExploreMarketingServices = () => {
    scrollToSection(marketingRef);
  };

  return (
    <div className="min-h-screen bg-white font-sans selection:bg-primary/20">
      
      {/* Google Font Import */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;900&display=swap');
        
        .font-serif {
          font-family: 'Playfair Display', serif;
        }
        
        .font-numbers {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
        }
      `}</style>
      
      {/* SECTION 0: HERO */}
      <section className="pt-32 pb-20 px-5 flex flex-col items-center text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-serif text-slate-900 mb-8 tracking-tight">
            Build. Automate. Scale.
          </h1>
          <p className="text-lg md:text-xl text-slate-500 font-medium max-w-2xl mx-auto leading-relaxed mb-10">
            We help startups, creators, and businesses build digital products, 
            AI systems, and growth engines.
          </p>
          <button 
            onClick={handleGetStarted}
            className="bg-[#0f172a] hover:bg-black text-white px-8 py-4 rounded-xl font-semibold flex items-center gap-3 transition-all duration-300 shadow-lg group mx-auto cursor-pointer"
          >
            Get Started
            <MoveRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      {/* SECTION 1: OUR CORE SERVICES */}
      <section className="py-20 px-5 bg-slate-50/50">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-slate-900 mb-4">Our Core Services</h2>
            <p className="text-slate-500 font-medium">A quick overview of our primary offerings. Click to explore more details below.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                title: "Web Development", 
                icon: <Code className="text-sky-500" />, 
                desc: "Custom websites, e-commerce solutions, and Web3 integration.", 
                link: "Explore Web Services",
                onClick: handleExploreWebServices
              },
              { 
                title: "App Development", 
                icon: <Tablet className="text-sky-500" />, 
                desc: "Native and cross-platform mobile applications for startups and businesses.", 
                link: "Explore App Services",
                onClick: handleExploreAppServices
              },
              { 
                title: "AI Agentic Solutions", 
                icon: <Cpu className="text-sky-500" />, 
                desc: "Develop custom AI chatbots, automation agents, and content generation systems.", 
                link: "Explore AI Solutions",
                onClick: handleExploreAISolutions
              },
              { 
                title: "Digital Marketing", 
                icon: <BarChart3 className="text-sky-500" />, 
                desc: "Drive growth with expert SEO, ads management, and conversion optimization.", 
                link: "Explore Marketing Services",
                onClick: handleExploreMarketingServices
              }
            ].map((s, i) => (
              <div key={i} className="bg-white p-8 rounded-[32px] border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-sky-50 rounded-xl flex items-center justify-center mb-6">{s.icon}</div>
                <h3 className="text-xl font-serif text-slate-900 mb-4">{s.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-8">{s.desc}</p>
                <button 
                  onClick={s.onClick}
                  className="text-sky-600 font-bold text-sm flex items-center gap-2 hover:gap-3 transition-all cursor-pointer"
                >
                  {s.link} <MoveRight size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2: WEB DEVELOPMENT */}
      <section ref={webDevRef} className="py-24 px-5 scroll-mt-24" id="web-development">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-sky-100 text-sky-600 rounded-full text-sm font-semibold mb-4">Web Development</span>
            <h2 className="text-4xl font-serif text-slate-900 mb-4">Web Development Services</h2>
            <p className="text-slate-500">Custom web solutions tailored to your business needs</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <PricingCard 
              title="Business Website Development" 
              price="₹10,000 – ₹79,999" 
              features={["Responsive UI/UX", "SEO-ready structure", "Lead forms integration", "Fast loading performance"]}
            />
            <PricingCard 
              title="E-Commerce Website Development" 
              price="₹11,999 – ₹1,49,999" 
              popular 
              features={["Payment gateway integration", "Product & order dashboard", "Inventory tracking", "Mobile optimized store"]}
            />
            <PricingCard 
              title="Web3 / Blockchain Website" 
              price="₹12,999 – ₹2,19,999" 
              features={["Wallet integration", "Token dashboard UI", "Smart contract interaction", "Secure frontend architecture"]}
            />
          </div>
        </div>
      </section>

      {/* SECTION 3: APP DEVELOPMENT */}
      <section ref={appDevRef} className="py-24 px-5 bg-slate-50/50 scroll-mt-24" id="app-development">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-purple-100 text-purple-600 rounded-full text-sm font-semibold mb-4">App Development</span>
            <h2 className="text-4xl font-serif text-slate-900 mb-4">App Development Services</h2>
            <p className="text-slate-500">Native and cross-platform mobile applications</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard 
              title="Business Mobile App" 
              price="₹10,999 – ₹1,89,999" 
              popular 
              features={["Android/iOS app", "Backend APIs", "Admin dashboard", "Push notifications"]}
            />
            <PricingCard 
              title="Startup MVP App" 
              price="₹12,999 – ₹1,49,999" 
              features={["Rapid MVP build", "Core feature development", "Scalable backend", "Investor-ready product"]}
            />
          </div>
        </div>
      </section>

      {/* SECTION 4: AI AGENTIC SOLUTIONS */}
      <section ref={aiRef} className="py-24 px-5 scroll-mt-24" id="ai-solutions">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-600 rounded-full text-sm font-semibold mb-4">AI Solutions</span>
            <h2 className="text-4xl font-serif text-slate-900 mb-4">AI Agentic Solutions</h2>
            <p className="text-slate-500">Intelligent automation and AI-powered tools</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <PricingCard 
              title="AI Business Chatbot" 
              price="₹14,999 – ₹1,19,999" 
              features={["Support automation", "Lead capture", "CRM integration"]}
            />
            <PricingCard 
              title="AI Sales Automation Agent" 
              price="₹15,999 – ₹1,59,999" 
              popular 
              features={["Email automation", "Follow-ups", "Funnel logic"]}
            />
            <PricingCard 
              title="AI Content Generation" 
              price="₹16,999 – ₹1,29,999" 
              features={["Social media AI", "Blogs & captions", "Ad copy"]}
            />
          </div>
        </div>
      </section>

      {/* SECTION 5: DIGITAL MARKETING */}
      <section ref={marketingRef} className="py-20 px-5 scroll-mt-24" id="digital-marketing">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-semibold mb-4">Digital Marketing</span>
            <h2 className="text-4xl font-serif text-slate-900 mb-4">Digital Marketing Services</h2>
            <p className="text-slate-500">Data-driven growth strategies for your business</p>
          </div>
          <div className="bg-[#0f172a] rounded-[40px] p-8 md:p-16 flex flex-col md:flex-row items-center gap-12 text-white overflow-hidden shadow-2xl relative">
            <div className="flex-1 space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                  <BarChart3 size={20}/>
                </div>
                <h3 className="text-2xl font-serif">Full-Stack Growth Solutions</h3>
              </div>
              <p className="text-slate-400">Scale your business with our comprehensive digital marketing services.</p>
              <ul className="space-y-3">
                {["Meta & Google Ads setup", "SEO optimization", "Funnel design", "Conversion tracking", "Growth strategy"].map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-300">
                    <Check size={16} className="text-emerald-500" /> {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full md:w-[350px] bg-slate-800/30 border border-slate-700/50 rounded-3xl p-8 text-center space-y-6">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-bold">Setup Price</p>
                <p className="text-2xl font-bold font-numbers">₹15,999 – ₹79,999</p>
              </div>
              <div className="pt-6 border-t border-slate-700">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 font-bold">Monthly Retainer</p>
                <p className="text-2xl font-bold font-numbers">₹17,000 – ₹60,000</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: PRICING SUMMARY */}
      <section ref={pricingRef} className="py-24 px-5 bg-slate-50/50 scroll-mt-24" id="pricing">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif text-slate-900 mb-4">Pricing Summary</h2>
            <p className="text-slate-500">Choose the plan that fits your business needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <SummaryCard 
              type="Starter"
              subtitle="Best for small businesses"
              price="₹49K – ₹89K"
              desc="For individuals and small teams looking to launch a basic, professional web presence."
            />
            <SummaryCard 
              type="Growth"
              subtitle="Best for startups & SMBs"
              price="₹90K – ₹1.6L"
              desc="For businesses that need a robust, feature-rich platform to drive growth and engagement."
              popular
            />
            <SummaryCard 
              type="Premium"
              subtitle="Best for enterprises"
              price="₹1.6L – ₹2.5L"
              desc="For companies requiring custom, high-performance solutions with advanced AI and automation."
            />
          </div>
        </div>
      </section>

      {/* SECTION 7: FINAL CTA */}
      <section ref={contactRef} className="py-20 px-5 pb-32" id="contact">
        <div className="max-w-[1000px] mx-auto bg-gradient-to-r from-slate-900 to-slate-800 rounded-[40px] p-16 text-center space-y-10 shadow-2xl">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-serif text-white">Ready to build something powerful?</h2>
            <p className="text-slate-300 text-lg font-medium">Tell us your idea and we'll help you turn it into a scalable product.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/contact" 
              className="w-full sm:w-auto bg-white hover:bg-slate-100 text-slate-900 px-10 py-4 rounded-2xl font-bold shadow-lg transition-all text-center"
            >
              Book a Consultation
            </Link>
            <a 
              href="https://wa.me/919876543210" 
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-transparent border border-slate-600 hover:bg-slate-800 text-white px-10 py-4 rounded-2xl font-bold transition-all text-center"
            >
              Contact on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Reusable Component for Service Detail Cards
const PricingCard = ({ title, price, features, popular = false }) => (
  <div className={`relative bg-white p-10 rounded-[35px] border ${popular ? 'border-sky-400 ring-1 ring-sky-400 shadow-xl' : 'border-slate-100 shadow-sm'} transition-transform hover:-translate-y-1`}>
    {popular && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-sky-500 text-white text-[10px] font-black px-4 py-1.5 rounded-full tracking-widest uppercase">
        Popular
      </span>
    )}
    <div className="flex items-center gap-3 mb-8">
      <div className="w-8 h-8 bg-sky-50 rounded-lg flex items-center justify-center">
        <Code size={16} className="text-sky-500" />
      </div>
      <h3 className="font-serif text-slate-900 text-lg">{title}</h3>
    </div>
    <div className="mb-10 text-3xl font-bold text-slate-900 tracking-tight font-numbers">
      {price}
    </div>
    <ul className="space-y-5 mb-12">
      {features.map((f, i) => (
        <li key={i} className="flex items-center gap-3 text-slate-600 font-medium text-sm">
          <Check size={18} className="text-emerald-500 flex-shrink-0" /> {f}
        </li>
      ))}
    </ul>
    <Link 
      to="/contact" 
      className="w-full block text-center py-4 bg-slate-50 hover:bg-slate-100 text-slate-900 rounded-2xl font-bold transition-colors shadow-sm"
    >
      Enquire Now
    </Link>
  </div>
);

// Reusable Component for Summary Section
const SummaryCard = ({ type, subtitle, price, desc, popular = false }) => (
  <div className={`relative bg-white p-10 rounded-[40px] border flex flex-col items-center text-center ${popular ? 'border-sky-400 ring-1 ring-sky-400 shadow-xl scale-105 z-10' : 'border-slate-100 shadow-sm'}`}>
    {popular && (
      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#0ea5e9] text-white text-[9px] font-black px-6 py-2 rounded-full tracking-widest uppercase shadow-lg shadow-sky-200">
        Most Popular
      </span>
    )}
    <h3 className="text-2xl font-serif text-slate-900 mb-2">{type}</h3>
    <p className="text-slate-400 text-xs font-bold mb-6">{subtitle}</p>
    <div className="text-4xl font-bold text-sky-600 mb-8 tracking-tighter font-numbers">
      {price}
    </div>
    <p className="text-slate-500 text-sm leading-relaxed mb-10 font-medium px-2">{desc}</p>
    <Link 
      to="/contact" 
      className={`w-full block text-center py-4 rounded-2xl font-bold transition-all shadow-md ${popular ? 'bg-sky-500 text-white hover:bg-sky-600' : 'bg-slate-50 text-slate-900 hover:bg-slate-100'}`}
    >
      Get a Quote
    </Link>
  </div>
);

export default ServicesPage;